import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {Button, Popconfirm, Tabs} from 'antd';
import {Shell} from 'components/Shell';
import {observer} from 'mobx-react-lite';
import {AppRoutePaths} from 'routing/Routes';
import {VehicleState} from 'store/vehicle/VehicleState';
import {VehicleAdditionalPhotos} from './forms/VehicleAdditionalPhotos';
import {VehicleEquipmentFormTable} from './forms/VehicleEquipmentFormTable';
import {VehicleFiles} from './forms/VehicleFiles';
import {VehicleKeySpecificationsForm} from './forms/VehicleKeySpecificationsForm';
import {VehicleOverviewForm} from './forms/VehicleOverviewForm';
import {VehicleParkingSpotAttributesForm} from './forms/VehicleParkingSpotAttributesForm';
import {VehiclePhotos} from './forms/VehiclePhotos';
import {VehicleServiceDataForm} from './forms/VehicleServiceDataForm';

export enum VehicleEditKey {
  keySpecifications = 'keySpecifications',
  photos = 'photos',
  overview = 'overview',
  serviceData = 'serviceData',
  parkingSpotAttributes = 'parkingSpotAttributes',
  carEquipment = 'carEquipment',
  files = 'files',
  additionalPhotos = 'additionalPhotos',
}

export const VehicleEditView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const {garageId, vehicleId} = useParams();

  const [state] = useState(() => new VehicleState());

  const [currentTab, setCurrentTab] = useState<VehicleEditKey>(VehicleEditKey.keySpecifications);

  useEffect(() => {
    state.loadVehicle(parseInt(vehicleId ?? '0'));
  }, [garageId, vehicleId, state]);

  const handleChange = useCallback(
    (key: VehicleEditKey) => {
      if (key !== currentTab && state.dirty[currentTab]) {
        const res = window.confirm('Are you sure you want to change tabs? You will lose unsaved changes.');
        if (res) {
          setCurrentTab(key);
        }
      } else {
        setCurrentTab(key);
      }
    },
    [currentTab]
  );

  return (
    <Shell
      title={t(vehicleId ? 'Edit Car' : 'Add Car')}
      backLink={vehicleId ? AppRoutePaths.garages.url + '/' + garageId + '/vehicles/' + vehicleId : true}
      actionButtons={
        vehicleId
          ? [
              <Popconfirm
                key="delete"
                placement="bottomRight"
                title={t('Are you sure you want to remove this car?')}
                onConfirm={() => state.removeVehicle()}
                okText={t('Yes')}
                cancelText={t('No')}
              >
                <Button key="remove" className="shell-btn" size="large">
                  {t('Remove car')}
                </Button>
              </Popconfirm>,
            ]
          : []
      }
    >
      <Tabs
        tabPosition={'left'}
        activeKey={currentTab}
        onTabClick={(key) => {
          handleChange(key as VehicleEditKey);
        }}
        // onChange={handleChange}
        items={[
          {
            label: 'Key specifications',
            key: VehicleEditKey.keySpecifications,
            children: <VehicleKeySpecificationsForm state={state} />,
          },
          {
            label: 'Photos',
            key: VehicleEditKey.photos,
            children: <VehiclePhotos state={state} />,
          },
          {
            label: 'Overview',
            key: VehicleEditKey.overview,
            children: <VehicleOverviewForm state={state} />,
          },
          {
            label: 'Service data',
            key: VehicleEditKey.serviceData,
            children: <VehicleServiceDataForm state={state} />,
          },
          {
            label: 'Parking spot attributes',
            key: VehicleEditKey.parkingSpotAttributes,
            children: <VehicleParkingSpotAttributesForm state={state} />,
          },
          {
            label: 'Car equipment',
            key: VehicleEditKey.carEquipment,
            children: <VehicleEquipmentFormTable state={state} />,
          },
          {
            label: 'Files',
            key: VehicleEditKey.files,
            children: <VehicleFiles state={state} />,
          },
          {
            label: 'Additional photos',
            key: VehicleEditKey.additionalPhotos,
            children: <VehicleAdditionalPhotos state={state} />,
          },
        ]}
      />
    </Shell>
  );
});
